import { Link } from "react-router-dom";
import { ArrowRight, Check, X } from "lucide-react";
import { FadeUp, SectionTag } from "../components/site/Reveal";
import { PHASES, PHILOSOPHY, ROY_PHOTO_BW } from "../content";

const FIT_YES = [
  "You are raising capital and want the room read before you pitch it",
  "You are breaking into a new market or taking share from an entrenched competitor",
  "You are preparing a company for a $30M to $100M exit",
  "You want a seasoned dealmaker beside you in high-stakes negotiations",
];

const FIT_NO = [
  "You want a cheerleader instead of a direct read",
  "Your project has no conviction behind it. Nobody architects sandcastles",
  "You want to move slowly. Slow deals are dying deals",
  "You confuse activity with progress",
];

const GROUND_RULES = [
  {
    title: "Speed",
    body: "You move at the pace the deal demands. When the window opens, you are already through it.",
  },
  {
    title: "Absolute transparency",
    body: "You get the truth about your position, your numbers, and your odds. Especially when it is uncomfortable. That is what you are paying for.",
  },
  {
    title: "Direct feedback",
    body: "No diplomacy tax. If your story is weak, it gets fixed. If your ask is wrong, it gets restructured. Before the market tells you.",
  },
  {
    title: "Aligned incentives",
    body: "Your win is the scoreboard. Every engagement is structured around your capital raised, your deal closed, your exit executed.",
  },
];

export default function WorkingWithMe() {
  return (
    <main data-testid="working-page" className="pt-24">
      <section className="relative overflow-hidden border-b border-gold/25 bg-cream">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 py-24 lg:grid-cols-2 lg:px-10">
          <FadeUp>
            <SectionTag>Working With Roy</SectionTag>
            <h1 className="mt-4 font-serif text-4xl font-bold tracking-tight text-ink sm:text-5xl lg:text-6xl">
              What changes with an <span className="gold-text italic">architect</span> in your corner
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
              You do not receive consulting and get left alone. You build a scalable
              business with an architect beside you: accessing opportunities, creating
              measurable growth, and activating your outcome together.
            </p>
          </FadeUp>
          <FadeUp delay={0.15}>
            <div className="relative">
              <div className="absolute -inset-3 border border-gold/60" />
              <img
                src={ROY_PHOTO_BW}
                alt="Roy Buncome portrait"
                className="h-[440px] w-full object-cover object-top"
                data-testid="working-portrait-img"
              />
            </div>
          </FadeUp>
        </div>
      </section>

      <section data-testid="phases-section" className="mx-auto max-w-7xl px-6 py-24 lg:px-10">
        <FadeUp>
          <SectionTag>Your Engagement</SectionTag>
          <h2 className="mt-4 font-serif text-3xl font-semibold tracking-tight text-ink sm:text-4xl">
            Four phases, one outcome: yours
          </h2>
        </FadeUp>
        <div className="mt-14 grid gap-6 lg:grid-cols-2">
          {PHASES.map((p, i) => (
            <FadeUp key={p.num} delay={i * 0.07}>
              <article
                data-testid={`phase-card-${p.num}`}
                className="group flex h-full gap-6 border border-gold/25 bg-white p-8 transition-all duration-500 hover:border-golddeep/60 hover:shadow-[0_20px_50px_-20px_rgba(140,109,31,0.35)]"
              >
                <span className="font-serif text-5xl font-bold text-gold/40 transition-colors duration-500 group-hover:text-gold">
                  {p.num}
                </span>
                <div>
                  <h3 className="font-serif text-2xl font-semibold text-ink">{p.title}</h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-slate-600">{p.body}</p>
                </div>
              </article>
            </FadeUp>
          ))}
        </div>
      </section>

      <section data-testid="philosophy-section" className="border-y border-gold/25 bg-cream">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10">
          <FadeUp>
            <SectionTag>The Operating System</SectionTag>
            <h2 className="mt-4 font-serif text-3xl font-semibold tracking-tight text-ink sm:text-4xl">
              Clarity. Focus. <span className="gold-text italic">Action.</span>
            </h2>
            <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-slate-600">
              Every engagement runs on this discipline. It is how your week turns into
              measurable growth instead of motion.
            </p>
          </FadeUp>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {PHILOSOPHY.map((p, i) => (
              <FadeUp key={p.title} delay={i * 0.08}>
                <article data-testid={`philosophy-card-${i}`} className="h-full border border-gold/30 bg-white p-8">
                  <span className="font-mono text-xs text-golddeep/60">0{i + 1}</span>
                  <h3 className="mt-4 font-serif text-2xl font-semibold text-ink">{p.title}</h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-slate-600">{p.body}</p>
                </article>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="fit-section" className="mx-auto max-w-7xl px-6 py-24 lg:px-10">
        <FadeUp>
          <SectionTag>Fit</SectionTag>
          <h2 className="mt-4 font-serif text-3xl font-semibold tracking-tight text-ink sm:text-4xl">
            Who this is for. And who it is not.
          </h2>
        </FadeUp>
        <div className="mt-14 grid gap-6 md:grid-cols-2">
          <FadeUp>
            <div data-testid="fit-yes-card" className="h-full border border-emerald2/30 bg-white p-8">
              <h3 className="font-serif text-xl font-semibold text-emerald2">Your name belongs here if</h3>
              <ul className="mt-6 space-y-4">
                {FIT_YES.map((f) => (
                  <li key={f} className="flex gap-3 text-sm leading-relaxed text-slate-700">
                    <Check size={16} className="mt-0.5 shrink-0 text-emerald2" /> {f}
                  </li>
                ))}
              </ul>
            </div>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div data-testid="fit-no-card" className="h-full border border-red-800/25 bg-white p-8">
              <h3 className="font-serif text-xl font-semibold text-red-800">Keep scrolling if</h3>
              <ul className="mt-6 space-y-4">
                {FIT_NO.map((f) => (
                  <li key={f} className="flex gap-3 text-sm leading-relaxed text-slate-500">
                    <X size={16} className="mt-0.5 shrink-0 text-red-800" /> {f}
                  </li>
                ))}
              </ul>
            </div>
          </FadeUp>
        </div>
      </section>

      <section data-testid="ground-rules-section" className="border-t border-gold/25 bg-cream">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10">
          <FadeUp>
            <SectionTag>Ground Rules</SectionTag>
            <h2 className="mt-4 font-serif text-3xl font-semibold tracking-tight text-ink sm:text-4xl">
              Non-negotiables that protect your outcome
            </h2>
          </FadeUp>
          <div className="mt-14 grid gap-px bg-gold/25 sm:grid-cols-2">
            {GROUND_RULES.map((g, i) => (
              <FadeUp key={g.title} delay={i * 0.06}>
                <article data-testid={`rule-card-${i}`} className="h-full bg-cream p-10">
                  <span className="font-mono text-xs text-golddeep/60">0{i + 1}</span>
                  <h3 className="mt-4 font-serif text-xl font-semibold text-ink">{g.title}</h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-slate-600">{g.body}</p>
                </article>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.15}>
            <div className="mt-16 text-center">
              <p className="font-serif text-2xl italic text-ink">
                Sound like the room you want to be in?
              </p>
              <Link to="/book" data-testid="working-book-btn" className="btn-gold mt-6">
                Book the 20-Minute Call <ArrowRight size={16} />
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
